/* raw-stand-cart.js - The Raw Comics Stand: the REAL Shopify cart (window.RawStandCart).
   Builder D, 2026-10-07 (mt4 work order D; Jake M-092..M-096: "Cart buttons and the Review-box Enter use
   Shopify's cart ... the top-bar count is the real cart count"). Plain JS, no libraries.
   Design note per S-build (~/AI/FLEET/standards/S-build.md 1b usability first, 3e explicit interfaces,
   4b tested with MUST-FAILs: tools/test_stand_cart.js): one job, the cart. The Review box is builder U's.

   add(book)        -> Promise<{ count, item }>   POST cart/add.js { items: [{ id: book.vid, quantity: 1 }] }
                                                   count is null if the add worked but re-reading the cart failed
   addMany(books)   -> Promise<{ count, added: [book], failed: [{ book, error }] }>   one at a time, never all-or-nothing
   count()          -> Promise<number>            GET cart.js
   state()          -> Promise<{ count, vids: Set<variant id> }>   which books are already in the cart
   onChange(fn)     -> unsubscribe()              fn(count, { vids, reason: 'add' | 'refresh' })
   Every change rewrites the theme's [data-cart-count] badge(s) and clears localStorage 'rc-cart-count'
   (main.js's static-demo stub would paint a stale count from it; see paint()).
   Adds are queued, so two quick taps never race. Errors reject with a message written for a shopper;
   err.status carries the HTTP status (422 = Shopify refused the add, e.g. the one copy is already in the cart). */
(function (root) {
  'use strict';

  var listeners = [];
  var queue = Promise.resolve();
  var lastVids = new Set();

  function shopRoot() {
    var s = root.Shopify && root.Shopify.routes && root.Shopify.routes.root;
    return typeof s === 'string' && s ? s : '/';
  }

  function request(path, init) {
    var f = root.fetch;
    if (typeof f !== 'function') return Promise.reject(new Error('This browser cannot reach the cart.'));
    init = init || {};
    init.credentials = 'same-origin';
    init.headers = Object.assign({ Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, init.headers || {});
    return Promise.resolve()
      .then(function () { return f(shopRoot() + path, init); })
      .then(null, function (err) {
        var e = new Error('Could not reach the cart. Check your connection and try again.');
        e.cause = err; e.network = true; throw e;
      })
      .then(function (r) {
        return r.text().then(function (t) {
          var j = null;
          try { j = t ? JSON.parse(t) : null; } catch (x) { j = null; }
          if (!r.ok) {
            // Shopify's cart errors: { status, message, description } - description is the readable one
            var msg = j && (j.description || j.message);
            var e = new Error(msg ? String(msg) : r.status >= 500 || r.status === 429
              ? 'The shop is busy right now (HTTP ' + r.status + '). Please try again in a moment.'
              : 'The cart could not be updated (HTTP ' + r.status + ').');
            e.status = r.status;
            throw e;
          }
          if (j === null) throw new Error('The cart sent back something unexpected. Please reload the page.');
          return j;
        });
      });
  }

  function paint(count) {
    var doc = root.document;
    if (doc && doc.querySelectorAll) {
      var els = doc.querySelectorAll('[data-cart-count]');
      for (var i = 0; i < els.length; i++) { els[i].textContent = String(count); els[i].hidden = !(count > 0); }
    }
    // main.js's static-demo stub paints the badge from this key on every page load. Nothing on the live
    // theme writes it, so it is inert today; writing it here would show a stale count after checkout empties
    // the cart. Removing it keeps the stub inert and the Liquid {{ cart.item_count }} the only truth.
    try { root.localStorage.removeItem('rc-cart-count'); } catch (e) {}
  }

  function emit(count, reason) {
    for (var i = 0; i < listeners.length; i++) {
      try { listeners[i](count, { vids: lastVids, reason: reason }); } catch (e) { if (root.console) console.error(e); }
    }
  }

  function fromCart(cart, reason) {
    var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    var vids = new Set();
    (cart && Array.isArray(cart.items) ? cart.items : []).forEach(function (it) { vids.add(it.variant_id || it.id); });
    lastVids = vids;
    paint(count);
    emit(count, reason);
    return { count: count, vids: vids };
  }

  function refresh(reason) {
    return request('cart.js').then(function (cart) { return fromCart(cart, reason || 'refresh'); });
  }

  // quiet: addMany refreshes once at the end instead of after every book
  function addOne(book, quiet) {
    var vid = book && (book.vid != null ? book.vid : null);
    if (vid == null || vid === 0 || vid === '') {
      return Promise.reject(new Error('This book cannot be added to the cart right now.'));
    }
    if (book.avail === false) {
      var e = new Error('Sold out: ' + (book.t || 'this book') + ' cannot go in the cart.');
      e.soldOut = true;
      return Promise.reject(e);
    }
    return request('cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(vid), quantity: 1 }] })
    }).then(function (res) {
      var item = res && Array.isArray(res.items) ? res.items[0] : res;
      if (quiet) return { count: null, item: item || null };
      // The book IS in the cart once add.js answered; if only the count read fails, say so with count: null.
      return refresh('add').then(function (s) { return { count: s.count, item: item || null }; },
                                 function () { return { count: null, item: item || null }; });
    });
  }

  // Queued: each add waits for the one before it to finish (success or failure).
  function enqueue(fn) {
    var p = queue.then(fn, fn);
    queue = p.then(function () {}, function () {});
    return p;
  }

  function add(book) { return enqueue(function () { return addOne(book); }); }

  function addMany(books) {
    var list = Array.isArray(books) ? books.slice() : [];
    return enqueue(function () {
      var added = [], failed = [], i = 0;
      function step() {
        if (i >= list.length) {
          return refresh('add').then(function (s) { return { count: s.count, added: added, failed: failed }; },
                                     function () { return { count: null, added: added, failed: failed }; });
        }
        var b = list[i++];
        return addOne(b, true).then(function () { added.push(b); }, function (err) { failed.push({ book: b, error: err }); }).then(step);
      }
      return step();
    });
  }

  function count() { return refresh('refresh').then(function (s) { return s.count; }); }
  function state() { return refresh('refresh'); }

  function onChange(fn) {
    if (typeof fn !== 'function') return function () {};
    listeners.push(fn);
    return function () { listeners = listeners.filter(function (x) { return x !== fn; }); };
  }

  root.RawStandCart = { add: add, addMany: addMany, count: count, state: state, onChange: onChange };
})(typeof window !== 'undefined' ? window : globalThis);
